'use client'

import { NestedKeyOf, useTranslations } from 'next-intl'
import { ReactNode } from 'react'
import {
  Id,
  toast as nativeToast,
  ToastContent,
  ToastContentProps,
  ToastOptions,
  TypeOptions
} from 'react-toastify'

import Error from '@/assets/icons/toast/error.svg'
import Success from '@/assets/icons/toast/success.svg'
import TxLink from '@/assets/icons/toast/tx-link.svg'
import Warn from '@/assets/icons/toast/warn.svg'
import Button from '@/shared/ui/Button'

import S from './ToastProvider.module.scss'

interface ToastMessageProps {
  titleI18N?: NestedKeyOf<IntlMessages>
  title?: string
  descriptionI18n?: NestedKeyOf<IntlMessages>
  link?: string
  onClick?: () => void
}

interface ToastMessageBlockProps
  extends Omit<ToastContentProps, 'isPaused'>,
    ToastMessageProps {
  icon: ReactNode
}

const Message = ({
  closeToast,
  icon,
  descriptionI18n,
  titleI18N,
  title,
  link,
  onClick
}: ToastMessageBlockProps) => {
  const t = useTranslations()

  return (
    <div className={S.wrapper}>
      {icon}
      <div className={S.contentWrapper}>
        <span className={S.title}>
          {titleI18N ? t(titleI18N as any) : title}
        </span>
        {!!descriptionI18n && (
          <div className={S.description}>
            {link && (
              <a
                href={link}
                onClick={closeToast}
                target="_blank"
                rel="noopener noreferrer"
                className={S.descriptionLink}
              >
                {t(descriptionI18n as any)}
              </a>
            )}
            {onClick && (
              <Button
                onClick={() => {
                  onClick()
                  closeToast()
                }}
                className={S.descriptionButton}
              >
                {t(descriptionI18n as any)}
              </Button>
            )}
            {!!(link || onClick) && <TxLink className={S.txLinkIcon} />}
          </div>
        )}
      </div>
    </div>
  )
}

const Icons: { [key in TypeOptions]: ReactNode } = {
  info: <Warn className={S.warnIcon} />,
  success: <Success className={S.successIcon} />,
  warning: <Warn className={S.warnIcon} />,
  error: <Error className={S.errorIcon} />,
  default: <Success className={S.successIcon} />
}

const toastMethods: {
  [key in TypeOptions]: (
    content: ToastContent<unknown>,
    options?: ToastOptions<unknown> | undefined
  ) => Id
} = {
  info: nativeToast.info,
  success: nativeToast.success,
  warning: nativeToast.warn,
  error: nativeToast.error,
  default: nativeToast.success
}

export const showToast = ({
  type,
  titleI18N,
  title,
  descriptionI18n,
  onClick,
  link
}: { type: TypeOptions } & ToastMessageProps) => {
  toastMethods[type](
    <Message
      icon={<div className={S.iconWrapper}>{Icons[type]}</div>}
      closeToast={() => {}}
      toastProps={{
        position: 'top-left',
        type: type,
        isIn: true,
        key: Math.random(),
        toastId: Math.random(),
        draggablePercent: 50,
        theme: 'colored',
        deleteToast: () => {},
        collapseAll: () => {},
        transition: () => <></>,
        closeToast: () => {}
      }}
      data={undefined}
      titleI18N={titleI18N}
      title={title}
      descriptionI18n={descriptionI18n}
      link={link}
      onClick={onClick}
    />,
    { icon: false }
  )
}
